import { useState } from 'react';
import { SettingsShellContext } from './settingsShellContext';
import type { ComponentType, ReactNode } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface SettingsRailItem {
  id: string;
  title: string;
  /** Extra text the rail search matches besides the title. */
  keywords?: string;
  icon: ComponentType<{ className?: string }>;
  iconClassName?: string;
  to: string;
}

export interface SettingsRailGroup {
  id: string;
  title: string;
  items: SettingsRailItem[];
}

function filterGroups(
  groups: readonly SettingsRailGroup[],
  search: string,
): SettingsRailGroup[] {
  const q = search.trim().toLowerCase();
  const visible: SettingsRailGroup[] = [];
  for (const group of groups) {
    const items = q
      ? group.items.filter(
          (item) =>
            item.title.toLowerCase().includes(q) ||
            (item.keywords?.toLowerCase().includes(q) ?? false),
        )
      : group.items;
    if (items.length > 0) visible.push({ ...group, items });
  }
  return visible;
}

function SettingsRail({ groups }: { groups: readonly SettingsRailGroup[] }) {
  const gt = useGT();
  const { pathname } = useLocation();
  const [search, setSearch] = useState('');
  const visibleGroups = filterGroups(groups, search);

  return (
    <nav className="flex w-56 shrink-0 flex-col">
      <div className="relative mb-2 shrink-0">
        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
        <Input
          placeholder={gt('Search...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 pl-8 text-sm"
        />
      </div>
      {/* Sizes to its content, shrinks and scrolls internally when a long
          list overflows; overscroll-contain keeps the wheel from chaining
          into anything else. */}
      <div className="min-h-0 overflow-y-auto overscroll-contain rounded-lg border bg-card shadow-sm divide-y">
        {visibleGroups.map((group) => (
          <div key={group.id} className="p-2">
            <div className="px-2 pt-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-foreground/70">
              {group.title}
            </div>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const isActive =
                  pathname === item.to || pathname.startsWith(`${item.to}/`);
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <Link
                      to={item.to}
                      className={cn(
                        'flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors border border-transparent',
                        isActive
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-foreground/80 hover:bg-accent hover:text-accent-foreground hover:border-border',
                      )}
                    >
                      <Icon
                        className={cn(
                          'size-4 shrink-0',
                          !isActive && 'text-muted-foreground',
                          item.iconClassName,
                        )}
                      />
                      <span className="truncate">{item.title}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {visibleGroups.length === 0 && (
          <div className="px-3 py-3 text-sm text-muted-foreground">
            <T>No matching settings</T>
          </div>
        )}
      </div>
    </nav>
  );
}

/**
 * Pane-fit settings shell shared by organization and user settings: a
 * searchable, sectioned rail on the left and the section content scrolling
 * independently beside it, with no frame of its own.
 *
 * `-m-2` swallows the app <main>'s p-2 and the height adds it back, so the page
 * itself never scrolls; the `p-2` on the columns row restores the spacing.
 */
export function SettingsLayout({
  groups,
  hideRail = false,
  children,
}: {
  groups: readonly SettingsRailGroup[];
  /** Drill-in pages (a record nested under a section) hide the rail so they
   *  read as a child of a section rather than a top-level setting. */
  hideRail?: boolean;
  children: ReactNode;
}) {
  return (
    <DashboardLayout fill>
      <div className="-m-2 flex h-[calc(100%+1rem)] flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 gap-3 p-2">
          {!hideRail && <SettingsRail groups={groups} />}
          <div className="min-w-0 flex-1 overflow-y-auto overscroll-contain">
            <SettingsShellContext.Provider value>
              {children}
            </SettingsShellContext.Provider>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
